import router from 'next/router'
import React, { useEffect, useState } from 'react'

import {
  chakra,
  Flex,
  useColorModeValue,
  useColorMode,
  Text,
  Heading,
  Box,
  useDisclosure,
} from '@chakra-ui/react'
import { useInView } from 'react-intersection-observer'

import { SIGN_IN, SIGN_UP } from '@/lib/constants/authConstants'
import { motion, useAnimation } from 'framer-motion'

import CustomImage from '@/components/Image/CustomImage'
import { basicTextColor, basicTextRgba } from '@/lib/constants/colorConstants'
import NavBar from '@/components/partner/NavBar'
import CustomButton from '../Button'
import { moveUp } from '@/lib/animations'
import HelpModal from '../Help/HelpModal'

type Menu = {
  name: string
  path: string
}

interface IProps {
  menus: Menu[]
  canStick: boolean
  triggerNav: boolean
  heroText: {
    heading: string
    paragraphFirst: string
    paragraphSecond: string
  }
}

export const Hero = ({ heroText, triggerNav }: IProps) => {
  const { toggleColorMode: toggleMode, colorMode } = useColorMode()
  const [becomePartner, setBecomePartner] = useState<boolean>(false)
  const { isOpen, onClose, onOpen } = useDisclosure()

  const bg = useColorModeValue('white', 'black')
  const controls = useAnimation()
  const { inView, ref } = useInView()

  useEffect(() => {
    if (inView) {
      controls.start('visible')
    }
  }, [controls, inView])

  const handleBecomePartner = () => {
    setBecomePartner(true)
    onOpen()
  }

  const goToPage = (page: string) => {
    const host =
      process.env.NEXT_PUBLIC_VERCEL_ENV === 'production'
        ? process.env.NEXT_PUBLIC_APP_HOST
        : process.env.NEXT_PUBLIC_APP_LOCALHOST

    if (page === SIGN_IN) {
      return router.push(host + '/login')
    }
    return router.push(host + '/signup')
  }

  return (
    <chakra.div
      id="home"
      height={{ base: 'auto', md: 'auto' }}
      w="full"
      bg={bg}
      pb="4rem"
      transition="background 0.2s"
      overflowY="hidden"
      position="relative"
      as={motion.div}
      animate={controls}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      <NavBar canStick={triggerNav} menus={[]} />
      <Flex
        mx="auto"
        px={['1rem', '3rem', '4rem', '6rem']}
        mt={{ base: '7rem', md: '8.5rem' }}
        alignItems="center"
        justifyContent="space-between"
        as={motion.div}
        variants={moveUp}
        columnGap="1rem"
        maxW="90rem"
        position={'relative'}
        h="full"
      >
        <Flex flexDir="column" flex={5}>
          <Flex
            flexDirection="column"
            flexWrap="wrap"
            alignItems={{ base: 'center', md: 'start' }}
          >
            <Heading
              as="h1"
              textAlign={{ base: 'center', md: 'start' }}
              fontSize={['1.5rem', '1.8rem', '2.3rem', '2.62rem']}
              fontWeight="semibold"
              color={basicTextColor[colorMode]}
              maxW={'35.5rem'}
              lineHeight={['35px', '45px', '63px']}
              mb="1.8rem"
            >
              {heroText.heading}
            </Heading>
            <Box
              maxW={'35.75rem'}
              textAlign={{ base: 'center', md: 'justify' }}
              fontSize={{ base: '1rem', md: '1.3rem' }}
              fontWeight={'medium'}
              lineHeight="33px"
              color={basicTextRgba[colorMode]}
              mb="3.1rem"
            >
              <Text textAlign={{ base: 'center', md: 'left' }}>
                {heroText.paragraphFirst}
              </Text>
              <Text textAlign={{ base: 'center', md: 'left' }}>
                {heroText.paragraphSecond}
              </Text>
            </Box>
          </Flex>
          <Flex
            alignItems="center"
            justifyContent={{ base: 'center', md: 'start' }}
          >
            <CustomButton
              mr="1.81rem"
              maxW="27rem"
              h={['2.8rem', '3rem']}
              borderRadius={'5px'}
              fontWeight="medium"
              fontSize={{ base: '0.8rem', md: '1rem' }}
              onClick={handleBecomePartner}
            >
              Get Started
            </CustomButton>
            {becomePartner && (
              <HelpModal
                isOpen={isOpen}
                onClose={onClose}
                partnerAllow={true}
                onClick={onClose}
              />
            )}
          </Flex>
        </Flex>
        <Flex
          display={{ base: 'none', md: 'flex' }}
          flex={4}
          h="full"
          position={'relative'}
          justifyContent={'center'}
          alignItems="center"
        >
          <CustomImage
            position="absolute"
            src="/hero-img-partner.svg"
            width={310}
            height={314}
            fill
          />
        </Flex>
      </Flex>
    </chakra.div>
  )
}
